<template>
    <!-- Mobile tab bar: Domov, Katalóg, Košík, Sprievodca, Menu (the account and Rázcestník are in the menu) -->
    <nav class="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200/70 pb-[env(safe-area-inset-bottom)]">
        <!-- White below the bar too: while iOS Chrome's toolbar slides in or out the bar can sit a bit above
             the screen's bottom edge, and the page would show through underneath -->
        <span aria-hidden="true" class="absolute inset-x-0 top-full h-24 bg-white"></span>
        <div class="grid grid-cols-5 h-16">
            <component v-for="item in items" :key="item.label" :is="item.to ? 'router-link' : 'button'"
                :to="item.to" @click="item.action?.()"
                class="relative flex flex-col items-center justify-center gap-0.5 transition-colors"
                :class="item.active ? 'text-brand-800' : 'text-gray-500 hover:text-brand-700'">
                <span v-if="item.active" class="absolute top-0 h-0.5 w-8 rounded-full bg-red-600"></span>
                <span class="relative">
                    <component :is="item.icon" class="h-6 w-6" :stroke-width="item.active ? 2.25 : 1.75" />
                    <span v-if="item.badge"
                        class="absolute -top-1.5 -right-2.5 bg-red-600 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-semibold ring-2 ring-white">
                        {{ item.badge }}
                    </span>
                </span>
                <span class="text-[11px] leading-none" :class="item.active ? 'font-semibold' : 'font-medium'">{{ item.label }}</span>
            </component>
        </div>
    </nav>

    <!-- Menu sheet (teleported: the blurred bar would be the containing block of a fixed child) -->
    <Teleport to="body">
        <Transition name="fade">
            <div v-if="isMenuOpen" class="lg:hidden fixed inset-0 z-[55] bg-brand-950/40" @click="closeMenu"></div>
        </Transition>
        <Transition name="sheet">
            <div v-if="isMenuOpen" ref="sheetRef" role="dialog" aria-label="Menu" :style="sheetStyle"
                @touchstart="onSheetTouchStart" @touchmove="onSheetTouchMove" @touchend="onSheetTouchEnd" @touchcancel="onSheetTouchEnd"
                class="lg:hidden fixed inset-x-0 bottom-0 z-[60] max-h-[85vh] overflow-y-auto overscroll-contain bg-white rounded-t-3xl shadow-2xl pb-[calc(1rem+env(safe-area-inset-bottom))] transition-transform duration-200">
                <div data-sheet-handle class="sticky top-0 bg-white flex items-center justify-between px-5 pt-3 pb-2">
                    <div class="absolute left-1/2 top-2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full"></div>
                    <h2 class="text-lg font-semibold text-brand-900 font-poppins pt-2">Menu</h2>
                    <button @click="closeMenu" aria-label="Zavrieť"
                        class="mt-2 w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100">
                        <X class="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                <!-- Account -->
                <div class="px-4 pb-3">
                    <div v-if="isAuthenticated" class="flex items-center gap-3 p-3 rounded-2xl bg-brand-50">
                        <UserAvatar :user="user" class="w-11 h-11 shrink-0 ring-2 ring-white" />
                        <div class="min-w-0">
                            <p class="font-semibold text-gray-900 font-poppins truncate">{{ user?.first_name || 'Používateľ' }}</p>
                            <p class="text-sm text-gray-600 truncate">{{ user?.email }}</p>
                        </div>
                    </div>
                    <div v-else class="grid grid-cols-2 gap-2">
                        <router-link to="/auth/login"
                            class="px-4 py-3 text-center rounded-xl border border-brand-200 text-brand-800 font-medium hover:bg-brand-50">
                            Prihlásenie
                        </router-link>
                        <router-link to="/auth/register"
                            class="px-4 py-3 text-center rounded-xl bg-brand-700 text-white font-medium hover:bg-brand-800">
                            Registrácia
                        </router-link>
                    </div>
                </div>

                <!-- Links -->
                <div class="px-2 border-t border-gray-100 pt-2">
                    <router-link v-for="link in menuLinks" :key="link.to" :to="link.to"
                        class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-800 hover:bg-brand-50"
                        :class="{ 'bg-brand-50 text-brand-900 font-semibold': isActive(link.to) }">
                        <component :is="link.icon" class="h-5 w-5 text-brand-600" />
                        <span class="flex-1 font-medium">{{ link.label }}</span>
                        <ChevronRight class="h-4 w-4 text-gray-300" />
                    </router-link>

                    <button v-if="isAuthenticated" @click="handleLogout"
                        class="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-700 hover:bg-red-50">
                        <LogOut class="h-5 w-5" />
                        <span class="font-medium">Odhlásiť sa</span>
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted, h } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { pagePath } from '@/brand'
import UserAvatar from '@/components/user/UserAvatar.vue'
import {
    Home,
    LayoutGrid,
    Signpost,
    ShoppingCart,
    User,
    Menu,
    X,
    ChevronRight,
    LogOut,
    ShoppingBag,
    Package,
    Settings,
    Info,
    MessageCircle,
    FileText,
    Shield,
    RotateCcw
} from 'lucide-vue-next'
import sprievodcaIcon from '@brand/assets/sprievodca.png'

// The pig chef as a mask filled with currentColor, so it takes the tab colours like the lucide icons
// (class falls through; stroke-width is ignored). The PNG fills its box, the lucide glyphs leave ~2px
// padding in their 24px box. Most of the pig's weight is the face (the hat is a thin cap on top), so at
// the lucide glyphs' height it looked small and low: it is drawn at 95% and 1px up, its chin level
// with the other icons' bottoms and the hat rising above their tops.
// Block like the lucide svgs (preflight): inline-block would sit on the text baseline and ride higher
const SprievodcaIcon = () => h('span', {
    'aria-hidden': 'true',
    style: {
        display: 'block',
        backgroundColor: 'currentColor',
        mask: `url(${sprievodcaIcon}) center / contain no-repeat`,
        WebkitMask: `url(${sprievodcaIcon}) center / contain no-repeat`,
        transform: 'translateY(-1px) scale(0.95)'
    }
})

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()

const isMenuOpen = ref(false)
const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)

const startsWith = (...prefixes) => prefixes.some((p) => route.path === p || route.path.startsWith(`${p}/`))
const isActive = (to) => route.path === to || route.path.startsWith(`${to}/`)

const items = computed(() => [
    { label: 'Domov', to: '/', icon: Home, active: !isMenuOpen.value && route.path === '/' },
    {
        label: 'Katalóg', to: '/products', icon: LayoutGrid,
        active: !isMenuOpen.value && startsWith('/products', '/sets')
    },
    {
        label: 'Košík', to: '/checkout/cart', icon: ShoppingCart,
        active: !isMenuOpen.value && startsWith('/checkout'),
        badge: cartStore.totalQuantity > 0 ? (cartStore.totalQuantity > 99 ? '99+' : cartStore.totalQuantity) : null
    },
    {
        label: 'Sprievodca', to: '/sprievodca', icon: SprievodcaIcon,
        active: !isMenuOpen.value && startsWith('/sprievodca')
    },
    { label: 'Menu', icon: Menu, active: isMenuOpen.value, action: () => { isMenuOpen.value = !isMenuOpen.value } }
])

const menuLinks = computed(() => [
    { label: 'Produkty', to: '/products', icon: ShoppingBag },
    { label: 'Rázcestník', to: '/categories', icon: Signpost },
    ...(isAuthenticated.value ? [
        { label: 'Môj účet', to: '/user', icon: User },
        { label: 'Moje objednávky', to: '/user/orders', icon: Package }
    ] : []),
    ...(authStore.isAdmin ? [{ label: 'Admin panel', to: '/admin', icon: Settings }] : []),
    { label: 'O nás', to: pagePath('about'), icon: Info },
    { label: 'Kontakt', to: pagePath('contact'), icon: MessageCircle },
    { label: 'Obchodné podmienky', to: pagePath('terms'), icon: FileText },
    { label: 'Ochrana osobných údajov', to: pagePath('privacy'), icon: Shield },
    { label: 'Reklamácie a vrátenie', to: pagePath('returns'), icon: RotateCcw }
])

const closeMenu = () => {
    isMenuOpen.value = false
}

const handleLogout = async () => {
    await authStore.logout()
    closeMenu()
    router.push('/')
}

// Close after navigating, also when tapping the current page's link
watch(() => route.fullPath, closeMenu)
const onMenuLinkClick = (event) => {
    if (event.target.closest?.('[role="dialog"] a')) closeMenu()
}

const onKeydown = (event) => {
    if (event.key === 'Escape') closeMenu()
}

// Pull the sheet down to close it: by the handle bar, or anywhere while the links are scrolled to the top
const sheetRef = ref(null)
const drag = ref(null) // { startY, startTime, offset, fromHandle } while a finger holds the sheet
const dismissing = ref(false)

const sheetStyle = computed(() => {
    if (dismissing.value) return { transform: 'translateY(100%)' }
    if (drag.value?.offset) return { transform: `translateY(${drag.value.offset}px)`, transition: 'none' }
    return null
})

const onSheetTouchStart = (event) => {
    const fromHandle = !!event.target.closest?.('[data-sheet-handle]')
    if (event.touches.length !== 1 || (!fromHandle && sheetRef.value?.scrollTop > 0)) return
    drag.value = { startY: event.touches[0].clientY, startTime: event.timeStamp, offset: 0, fromHandle }
}

const onSheetTouchMove = (event) => {
    const current = drag.value
    if (!current) return
    const offset = event.touches[0].clientY - current.startY
    // An upward swipe on the links scrolls them as usual
    if (offset < 0 && !current.offset && !current.fromHandle) {
        drag.value = null
        return
    }
    current.offset = Math.max(offset, 0)
    if (event.cancelable) event.preventDefault()
}

const onSheetTouchEnd = async (event) => {
    const current = drag.value
    drag.value = null
    if (!current?.offset) return
    const speed = current.offset / Math.max(event.timeStamp - current.startTime, 1)
    // Far enough or flicked: it slides on down from where it is (the leave transition can't, the
    // element is removed without its style being patched), then closes. Otherwise it springs back
    if (current.offset > 96 || speed > 0.5) {
        dismissing.value = true
        await nextTick()
        closeMenu()
    }
}

// No page scrolling behind the open sheet
watch(isMenuOpen, (open) => {
    document.body.style.overflow = open ? 'hidden' : ''
    if (open) dismissing.value = false
})

onMounted(() => {
    document.addEventListener('keydown', onKeydown)
    document.addEventListener('click', onMenuLinkClick)
})

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown)
    document.removeEventListener('click', onMenuLinkClick)
    document.body.style.overflow = ''
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.sheet-enter-active,
.sheet-leave-active {
    transition: transform 280ms cubic-bezier(0.32, 0.72, 0, 1);
}

.sheet-enter-from,
.sheet-leave-to {
    transform: translateY(100%);
}
</style>
